import type { ReactNode } from "react"
import Tooltip from "@/v2-ui/tooltip"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Link from "@/v2-backoffice-client/components/link"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import {
  deriveCompanyLinkStatus,
  getCompanyLinkStatusConfig,
  getCompanyLinkHealthyDisplay,
  COMPANY_LINK_STATUS_PENDING_PUSH,
} from "@/v2-backoffice-client/customer/company/customerCompany.constants"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import { formatMergeSurvivor } from "@/v2-backoffice-client/customer/merge/customer.merge.utils"

export type CustomerCompanyBadgeProps = {
  customer: CustomerFragment
  // When true the healthy "In sync" state is rendered as a plain dot too
  // (no tooltip) instead of null.
  showHealthy?: boolean
  // Optional repair affordance rendered at the foot of the tooltip (the
  // table cell passes the "Link company" button here so the action sits
  // next to the explanation). Tooltip content is hoverable, so it stays
  // clickable.
  action?: ReactNode
}

// CustomerCompanyBadge surfaces how a customer relates to its HubSpot
// company (derived from the acc.CustomerCompanies link row) — the customer
// sibling of UserContactBadge. Under the D0 app-wins contract the only
// divergence is "not pushed yet"; the tooltip explains the state and shows
// the name drift while a push is pending.
function CustomerCompanyBadge(props: CustomerCompanyBadgeProps) {
  const { customer, showHealthy, action } = props

  const status = deriveCompanyLinkStatus(customer)
  const config = getCompanyLinkStatusConfig(status)

  if (!config) {
    if (!showHealthy) return null
    const healthy = getCompanyLinkHealthyDisplay()
    return <DotText variant={healthy.variant}>{healthy.label}</DotText>
  }

  const showNameDrift =
    status === COMPANY_LINK_STATUS_PENDING_PUSH &&
    !!customer.Company?.Name &&
    !!customer.Name &&
    customer.Company.Name !== customer.Name

  // Where the merge went: the survivor's customer, linked to its panel so
  // the operator lands on the row the people belong on.
  const mergedInto = customer.MergedInto
  const survivorLink = mergedInto?.SurvivingCustomer?.ExternalCustomerId
    ? buildCustomerViewLink(mergedInto.SurvivingCustomer.ExternalCustomerId)
    : null

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <DotText variant={config.variant}>{config.label}</DotText>
      </Tooltip.Trigger>

      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex max-w-xs flex-col gap-3 p-3">
          <div className="flex flex-col gap-1">
            <DotText
              variant={config.variant}
              className="flex font-medium text-popover-foreground"
            >
              {config.label}
            </DotText>
            <p className="text-muted-foreground">{config.description}</p>

            {showNameDrift && (
              <div className="mt-1 flex flex-col gap-0.5 text-xs">
                <span>
                  <span className="text-muted-foreground">Backoffice: </span>
                  {customer.Name}
                </span>
                <span>
                  <span className="text-muted-foreground">HubSpot: </span>
                  {customer.Company?.Name}
                </span>
              </div>
            )}

            {mergedInto && (
              <div className="mt-1 flex flex-col gap-0.5 text-xs">
                <span>
                  <span className="text-muted-foreground">Merged into: </span>
                  {survivorLink ? (
                    <Link variant="muted" to={survivorLink}>
                      {formatMergeSurvivor(mergedInto)}
                    </Link>
                  ) : (
                    formatMergeSurvivor(mergedInto)
                  )}
                </span>
                <span>
                  <span className="text-muted-foreground">Company id: </span>
                  {mergedInto.MergedExternalCompanyId}
                </span>
              </div>
            )}

            <p className="mt-1 text-xs text-muted-foreground">{config.fix}</p>
          </div>

          {action && (
            <div className="border-t border-border pt-2">{action}</div>
          )}
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default CustomerCompanyBadge
